import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    TextField,
    Button,
    Grid,
    Typography,
    Box,
    CircularProgress,
    Paper,
    Chip,
    Avatar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { imagesRepo } from '../../../config';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: '#ffffff',
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: theme.spacing(1),
        border: '1px dashed #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: theme.spacing(20),
    },
    emptyIcon: {
        fontSize: 48,
        color: '#bdbdbd',
        marginBottom: theme.spacing(2),
    },
    infoCard: {
        padding: theme.spacing(2),
        backgroundColor: '#f8f9fa',
        borderRadius: theme.spacing(1),
        border: '1px solid #e9ecef',
        marginBottom: theme.spacing(3),
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    label: {
        color: '#6c757d',
        fontSize: '0.875rem',
        marginRight: theme.spacing(1),
    },
    value: {
        color: '#212529',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    joaillierName: {
        color: '#1976d2',
        fontWeight: 600,
        fontSize: '1rem',
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    submitButton: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5),
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.9375rem',
        borderRadius: theme.spacing(1),
        boxShadow: 'none',
        '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.spacing(1),
            backgroundColor: '#ffffff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
            },
        },
    },
}));

const validationSchema = Yup.object().shape({
    poidsR: Yup.number()
        .required('Poids retour obligatoire.')
        .typeError('Poids retour doit être un nombre.')
        .min(0, 'Le poids doit être positif.'),
    temps: Yup.number()
        .required('Temps obligatoire.')
        .typeError('Temps doit être un nombre.')
        .min(0, 'Le temps doit être positif.'),
});

const safeFieldValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toString() : '';
};

const formatWeight = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
};

const JoaillierRetourModule = forwardRef(({ 
    selectedItem, 
    onSubmit,
    loading = false,
    hideSubmitButton = false
}, ref) => {
    const classes = useStyles();
    const poidsRRef = useRef(null);
    const tempsRef = useRef(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    useImperativeHandle(ref, () => ({
        focusPoidsR: () => {
            if (poidsRRef.current) {
                poidsRRef.current.focus();
                if (typeof poidsRRef.current.select === 'function') {
                    poidsRRef.current.select();
                }
            }
        }
    }));

    const formik = useFormik({
        initialValues: {
            poidsR: '',
            temps: '',
        },
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (values) => {
            onSubmit(values);
        },
    });

    const { values, errors, handleChange, handleSubmit, resetForm } = formik;

    const handleFormSubmit = (event) => {
        if (event) {
            event.preventDefault();
        }
        setHasSubmitted(true);
        handleSubmit(event);
    };

    useEffect(() => {
        if (!selectedItem) {
            resetForm();
            setHasSubmitted(false);
        } else {
            // Pré-remplir avec les données existantes si rendu=1
            if (selectedItem.rendu === 1 || selectedItem.rendu === '1') {
                resetForm({
                    values: {
                        poidsR: safeFieldValue(selectedItem.poids_r),
                        temps: safeFieldValue(selectedItem.temps),
                    },
                });
            } else {
                // Vider les champs pour un nouveau retour (rendu=0)
                resetForm();
            }
            setHasSubmitted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    const handleKeyPress = async (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setHasSubmitted(true);
            const fieldName = e.target.name;
            
            // Valider le champ actuel
            await formik.validateField(fieldName);
            const fieldMeta = formik.getFieldMeta(fieldName);
            const fieldError = fieldMeta.error;
            
            if (!fieldError) {
                // Pas d'erreur, passer au champ suivant
                if (nextRef && nextRef.current) {
                    nextRef.current.focus();
                    if (typeof nextRef.current.select === 'function') {
                        nextRef.current.select();
                    }
                } else {
                    // Dernier champ, soumettre
                    formik.submitForm();
                }
            }
        }
    };

    return (
        <Box className={classes.root}>
            {selectedItem ? (
            <form onSubmit={handleFormSubmit}>
                <Grid container spacing={2} style={{ minHeight: 'auto' }}>
                    {/* COLONNE 1: Info du mouvement sélectionné */}
                    <Grid item xs={12} sm={6}>
                        {selectedItem ? (
                            <Paper className={classes.infoCard} elevation={0}>
                                <Grid container spacing={2}>
                                    {/* Avatar */}
                                    <Grid item xs={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Avatar 
                                            src={selectedItem.avatar ? `${imagesRepo}/avatar/${selectedItem.avatar}` : undefined}
                                            alt={selectedItem.label}
                                            style={{ 
                                                width: 60, 
                                                height: 60,
                                                backgroundColor: '#1976d2',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {!selectedItem.avatar && selectedItem.label ? selectedItem.label.charAt(0).toUpperCase() : '?'}
                                        </Avatar>
                                    </Grid>
                                    {/* Informations */}
                                    <Grid item xs={9}>
                                        <Typography className={classes.joaillierName}>
                                            {selectedItem.label}
                                        </Typography>
                                        
                                        <Box className={classes.infoRow}>
                                            <Typography className={classes.label}>N° OF:</Typography>
                                            <Chip 
                                                label={selectedItem.n_sachet} 
                                                size="small" 
                                                color="primary"
                                                style={{ fontWeight: 500 }}
                                            />
                                        </Box>
                                        
                                        <Box className={classes.infoRow}>
                                            <Typography className={classes.label}>Poids départ:</Typography>
                                            <Typography className={classes.value}>
                                                {formatWeight(selectedItem.poids_d)} g
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ) : null}
                    </Grid>

                    {/* COLONNE 2: Formulaire retour */}
                    <Grid item xs={12} sm={6}>
                        
                            <Box>
                                {/* Poids retour */}
                                <TextField
                                    fullWidth
                                    label="Poids retour"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    name="poidsR"
                                    value={values.poidsR}
                                    onChange={handleChange}
                                    onKeyPress={(e) => handleKeyPress(e, tempsRef)}
                                    error={hasSubmitted && !!errors.poidsR}
                                    helperText={hasSubmitted && errors.poidsR ? errors.poidsR : ' '}
                                    variant="outlined"
                                    disabled={loading}
                                    inputRef={poidsRRef}
                                    className={classes.textField}
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                />

                                {/* Temps */}
                                <TextField
                                    fullWidth
                                    label="Temps de travail"
                                    type="number"
                                    inputProps={{ step: "0.5", min: "0" }}
                                    name="temps"
                                    value={values.temps}
                                    onChange={handleChange}
                                    onKeyPress={(e) => handleKeyPress(e, null)}
                                    error={hasSubmitted && !!errors.temps}
                                    helperText={hasSubmitted && errors.temps ? errors.temps : ' '}
                                    variant="outlined"
                                    disabled={loading}
                                    inputRef={tempsRef}
                                    className={classes.textField}
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                />

                                {/* Bouton submit */}
                                {!hideSubmitButton && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={loading || !selectedItem}
                                        className={classes.submitButton}
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                        style={{ 
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        {loading ? 'Enregistrement...' : 'Enregistrer le retour'}
                                    </Button>
                                )}
                            </Box>
                        
                    </Grid>
                </Grid>
            </form>
            ) : (
                <Box className={classes.emptyState}>
                    <InfoOutlinedIcon className={classes.emptyIcon} />
                    <Typography variant="body1" color="textSecondary">
                        Sélectionnez un mouvement
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                        Cliquez sur une ligne pour enregistrer le retour
                    </Typography>
                </Box>
            )}
        </Box>
    );
});

JoaillierRetourModule.displayName = 'JoaillierRetourModule';

export default JoaillierRetourModule;
